import React from 'react'
import { useIntl } from '@jetshop/intl'
import MobileFilterSection from './MobileFilterSection'
import MultiSelectFilterOptions, {
   getSelectedValues,
} from './MultiSelectFilterOptions'
import PriceFilterContent, {
   formatKr,
   formatNumber,
   getSelectedPriceRange,
   usePriceBounds,
} from './PriceFilterContent'
import SortList from './SortList'
import { useSortOptions } from './sortOptions'
import {
   getStockStatusFilterOptions,
   getStockStatusLabel,
   useStockStatusOptions,
} from './stockStatusOptions'
import {
   DIGIFI_FILTER_FIELD_NAMES,
   DIGIFI_SORT_VALUES,
} from '../../DigifiSearch/constants'

const MOBILE_VISIBLE_OPTIONS = 6

const MULTI_SELECT_FILTER_FIELDS = [
   DIGIFI_FILTER_FIELD_NAMES.PRODUCT_TYPE,
   DIGIFI_FILTER_FIELD_NAMES.BRAND,
   DIGIFI_FILTER_FIELD_NAMES.STOCK_STATUS,
]

/**
 * Number of active filter values, shown as a badge on the mobile filter button. Sorting is not counted.
 */
export const getActiveFilterCount = (filters = []) => {
   const multiSelectCount = MULTI_SELECT_FILTER_FIELDS.reduce(
      (sum, fieldName) => sum + getSelectedValues(filters, fieldName).length,
      0
   )
   const hasPrice = !!getSelectedPriceRange(filters)
   const hasPrebook = filters.some(
      f =>
         f?.fieldName === DIGIFI_FILTER_FIELD_NAMES.PREBOOK &&
         (f.value === true || f.value === 'true')
   )
   return multiSelectCount + (hasPrice ? 1 : 0) + (hasPrebook ? 1 : 0)
}

/**
 * Filter sections in the mobile filter drawer: sorting, product type, brand, price and stock status.
 */
const MobileFilterList = ({
   filters,
   facets,
   updateFilters,
   hideFilters = [],
}) => {
   const t = useIntl()
   const sortOptions = useSortOptions()
   const stockStatusOptions = useStockStatusOptions()
   const priceBounds = usePriceBounds(facets)

   const isVisible = fieldName => !hideFilters.includes(fieldName)

   const currentSort =
      filters?.find(f => f?.fieldName === DIGIFI_FILTER_FIELD_NAMES.SORT_BY)
         ?.value ?? DIGIFI_SORT_VALUES.RELEVANCE
   const currentSortLabel = sortOptions.find(
      option => option.value === currentSort
   )?.label

   const selectedProductTypes = getSelectedValues(
      filters,
      DIGIFI_FILTER_FIELD_NAMES.PRODUCT_TYPE
   )
   const selectedBrands = getSelectedValues(
      filters,
      DIGIFI_FILTER_FIELD_NAMES.BRAND
   )
   const selectedStockStatuses = getSelectedValues(
      filters,
      DIGIFI_FILTER_FIELD_NAMES.STOCK_STATUS
   )
   const stockStatusFilterOptions = getStockStatusFilterOptions(
      stockStatusOptions,
      facets?.stockStatusFacets,
      selectedStockStatuses
   )
   const selectedPrice = getSelectedPriceRange(filters)

   return (
      <>
         {isVisible(DIGIFI_FILTER_FIELD_NAMES.SORT_BY) && (
            <MobileFilterSection
               title={t('sorting')}
               summary={currentSort ? currentSortLabel : null}
            >
               <SortList
                  large
                  fieldName={DIGIFI_FILTER_FIELD_NAMES.SORT_BY}
                  options={sortOptions}
                  filters={filters}
                  isSelected={({ value }) => value === currentSort}
                  onSortChange={({ fieldName, value }) =>
                     updateFilters?.([{ fieldName, value }])
                  }
               />
            </MobileFilterSection>
         )}

         {isVisible(DIGIFI_FILTER_FIELD_NAMES.PRODUCT_TYPE) &&
            (facets?.productTypeFacets?.length > 0 ||
               selectedProductTypes.length > 0) && (
               <MobileFilterSection
                  title={t('Product type')}
                  summary={selectedProductTypes.join(', ')}
               >
                  <MultiSelectFilterOptions
                     inline
                     fieldName={DIGIFI_FILTER_FIELD_NAMES.PRODUCT_TYPE}
                     filters={filters}
                     options={facets?.productTypeFacets}
                     updateFilters={updateFilters}
                     searchPlaceholder={t('Search product type...')}
                     emptyText={t('No product types found')}
                     sortBy="count"
                     selectedFirst
                     visibleLimit={MOBILE_VISIBLE_OPTIONS}
                     getShowAllLabel={count =>
                        t('Show all { count } product types', { count })
                     }
                  />
               </MobileFilterSection>
            )}

         {isVisible(DIGIFI_FILTER_FIELD_NAMES.BRAND) &&
            (facets?.brandFacets?.length > 0 || selectedBrands.length > 0) && (
               <MobileFilterSection
                  title={t('brand')}
                  summary={selectedBrands.join(', ')}
               >
                  <MultiSelectFilterOptions
                     inline
                     fieldName={DIGIFI_FILTER_FIELD_NAMES.BRAND}
                     filters={filters}
                     options={facets?.brandFacets}
                     updateFilters={updateFilters}
                     searchPlaceholder={t('Search brand...')}
                     emptyText={t('No brands found')}
                     sortBy="count"
                     selectedFirst
                     visibleLimit={MOBILE_VISIBLE_OPTIONS}
                     getShowAllLabel={count =>
                        t('Show all { count } brands', { count })
                     }
                  />
               </MobileFilterSection>
            )}

         {isVisible(DIGIFI_FILTER_FIELD_NAMES.PRICE) && priceBounds.max > 0 && (
            <MobileFilterSection
               title={t('Price')}
               summary={
                  selectedPrice
                     ? `${formatNumber(selectedPrice[0])} - ${formatKr(
                          selectedPrice[1]
                       )}`
                     : null
               }
            >
               <PriceFilterContent
                  filters={filters}
                  facets={facets}
                  updateFilters={updateFilters}
               />
            </MobileFilterSection>
         )}

         {isVisible(DIGIFI_FILTER_FIELD_NAMES.STOCK_STATUS) &&
            stockStatusFilterOptions.length > 0 && (
               <MobileFilterSection
                  title={t('stock-status')}
                  summary={selectedStockStatuses
                     .map(value =>
                        getStockStatusLabel(stockStatusOptions, value)
                     )
                     .join(', ')}
               >
                  <MultiSelectFilterOptions
                     inline
                     fieldName={DIGIFI_FILTER_FIELD_NAMES.STOCK_STATUS}
                     filters={filters}
                     options={stockStatusFilterOptions}
                     updateFilters={updateFilters}
                     searchable={false}
                     sortBy="none"
                  />
               </MobileFilterSection>
            )}
      </>
   )
}

export default MobileFilterList
